<script setup lang="ts">
import { mdiCloseCircle } from "@mdi/js";
import FeedsResults from "~/components/layout/Search/Feeds/FeedsResults.vue";
import ResultCard from "~/components/layout/Search/ResultCard.vue";
import SearchResultsLoading from "~/components/layout/Search/SearchResultsLoading.vue";
import Icon from "~/components/ui/Icon.vue";
import Skeleton from "~/components/ui/Skeleton.vue";
import { IMarketHeader } from "~/types/config/marketHeader";
import { IGame } from "~/types/gaming/game";
import { IEntityTop } from "~/types/graph-ql/graphql-types";
import type { IEvent } from "~/types/sports/event";
import type { IMarket } from "~/types/sports/market";
import type { IOutcome } from "~/types/sports/outcome";
import type { IPrice } from "~/types/sports/price";
import t from "../../../utils/translate";
import CasinoSearchResults from "../Casino/CasinoSearchResults.vue";

export interface IFollowingsResult {
	id: string;
	name: string;
	handle: string;
	type: string;
	category: string;
	related: { id: string; name: string; type: string }[];
	offer: {
		eventCount: number;
	};
}

interface ISearchResultsProps {
	emopData:
		| {
				events: Map<number, IEvent>;
				markets: Map<number, IMarket>;
				outcomes: Map<number, IOutcome>;
				prices: Map<number, IPrice>;
		  }
		| undefined;
	followings: IFollowingsResult[] | IEntityTop[] | null;
	filterMarkets: { name: string; displayName: string; filterIndex: number }[] | undefined;
	activeMarket: string | undefined;
	loading: boolean;
	marketHeaders?: { sport: string; sportMarkets: IMarketHeader[] }[];
	resultsLoading: boolean;
	homePage?: boolean;
	searchFilters?: string;
	casinoData?: IGame[];
	casinoLoading?: boolean;
	casinoError?: unknown;
	currentFollowings: { identifier: string; subscriptionType: string }[];
}

const emit = defineEmits<(event: "switchMarkets", value: string) => void>();

const props = withDefaults(defineProps<ISearchResultsProps>(), {
	casinoLoading: false,
});

const reactiveSearchFilters = computed(() => {
	if (props.searchFilters === "sport") return "sport";
	if (props.searchFilters === "casino") return "casino";
	return "all";
});

const showCasino = computed(() => {
	return reactiveSearchFilters.value === "casino" || reactiveSearchFilters.value === "all";
});
const showSport = computed(() => {
	return reactiveSearchFilters.value === "sport" || reactiveSearchFilters.value === "all";
});

const displayCasinoTiles = computed(() => props.casinoLoading || props.resultsLoading || props.loading);
</script>

<template>
	<div v-if="loading && showSport" class="h-full mt-4">
		<Skeleton class="w-[150px] h-[20px] bg-light-500 dark:bg-dark-300 ml-2 mb-2" />

		<div>
			<div class="flex pb-3 pt-1 gap-3 h-6 mb-3">
				<div v-for="n in 8" :key="n" class="h-3 w-14 bg-light-500 dark:bg-dark-300 animate-pulse rounded" />
			</div>
		</div>
		<SearchResultsLoading :home-page="homePage" />
	</div>

	<div v-else class="h-full">
		<template v-if="!homePage || showSport">
			<div
				v-if="(emopData && emopData?.events.size > 0 && emopData?.markets.size > 0) || loading || resultsLoading"
				:class="['mb-1 px-2 font-bold', homePage ? 'mt-3' : '']"
			>
				{{ t("search-match-results") }}:
			</div>
			<div class="flex gap-3 overflow-x-auto overflow-y-hidden mb-2 pb-1" id="sport-market-list-scroller" v-if="filterMarkets && emopData">
				<div
					v-for="market in filterMarkets"
					:key="market.name"
					class="text-xs font-semibold text-center transition-all border-b-2 cursor-pointer whitespace-nowrap"
					:class="activeMarket === market.name ? 'text-identity  border-b-identity sport-market-list-active' : 'border-b-[transparent]'"
					@click="emit('switchMarkets', market.name)"
				>
					<span class="min-w-[60px] block">
						{{ market.displayName }}
					</span>
				</div>
			</div>
			<div
				v-if="(!emopData || emopData.events.size === 0 || emopData.markets.size === 0) && !loading && !resultsLoading"
				class="flex items-center justify-center font-bold py-6"
			>
				{{ t("search-yielded-no-results") }}
			</div>
			<template v-else>
				<div
					v-if="!resultsLoading && emopData"
					v-for="(event, idx) in emopData.events.values()"
					:key="idx"
					class="bg-light-50 dark:bg-dark-800 rounded-2xl mb-2 text-xs"
				>
					<ResultCard
						:initial-state="idx < 3"
						:event="event"
						:markets="emopData.markets"
						:outcomes="emopData.outcomes"
						:prices="emopData.prices"
						:homePage="homePage"
						:marketHeaders="marketHeaders"
					/>
				</div>
				<SearchResultsLoading v-if="resultsLoading" :home-page="homePage" />
			</template>
		</template>
	</div>

	<div v-if="homePage && showCasino" class="mt-3">
		<strong class="pl-2 pb-1">{{ t("casino-results") }}:</strong>
		<CasinoSearchResults :data="casinoData" :error="casinoError" :isLoading="displayCasinoTiles" homePage />
		<div
			v-if="!displayCasinoTiles && (!casinoData || casinoData.length <= 0)"
			class="w-full bg-light-400 dark:bg-dark-700 flex p-1.5 gap-2 items-center rounded-lg"
		>
			<Icon :path="mdiCloseCircle" />
			<label class="text-sm">{{ t("no-results-found") }}</label>
		</div>
	</div>

	<div v-if="(showSport && !showCasino) || (showSport && showCasino)" class="mt-3">
		<FeedsResults v-if="!loading" :homePage="homePage" :followings="followings" />
		<template v-else>
			<Skeleton class="w-[150px] h-[20px] bg-light-500 dark:bg-dark-300 ml-2 mb-2" />
			<div class="flex flex-col gap-2 mb-3">
				<div v-for="n in 4" :key="n" class="w-full h-14 bg-light-500 dark:bg-dark-300 rounded-md animate-pulse" />
			</div>
		</template>
	</div>
</template>

<style scoped lang="scss">
::-webkit-scrollbar-track {
	@apply bg-light-400 dark:bg-dark-900;
}

::-webkit-scrollbar-thumb {
	@apply bg-light-900 dark:bg-dark-500;
}
</style>
